import { atom, useAtom } from 'jotai'
import { useGlobalStore } from '@highlight/ipc/react'
import { CSSProperties, useCallback, useMemo } from 'react'

export type FeatureHighlightIds =
  | 'chat-recent-history'
  | 'chat-shortcuts-list'
  | 'chat-updates-attachments'
  | 'chat-mentions'
  | 'titlebar-1'
  | 'titlebar-2'
  | 'titlebar-3'
  | 'memory-tab-intro'
  | 'getting-started-intro'

export type FixedComponentPosition = 'top' | 'bottom' | 'left' | 'right'
interface ActionProps {
  dismiss?: boolean
  label: string | React.ReactElement
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}
export interface FeatureHighlightProps {
  description?: string | React.ReactElement | (string | React.ReactElement)[]
  id: FeatureHighlightIds
  nextId?: FeatureHighlightIds
  previousId?: FeatureHighlightIds
  hide?: boolean
  offset?: number
  position: FixedComponentPosition
  fullWidth?: boolean
  alignment?: 'start' | 'center' | 'end'
  title?: string | React.ReactElement
  primaryAction?: ActionProps
  secondaryAction?: ActionProps
  style?: CSSProperties
  backgroundOverlay?: boolean
  dismissible?: boolean
  delay?: number
  wrapperStyle?: CSSProperties
  className?: string
  activeWrapperClassName?: string
  wrapperClassName?: string
  imageUrl?: string
  videoUrl?: string
  hint?: string | React.ReactElement
  usePortal?: boolean
  disabled?: boolean
  disabledWithModals?: boolean
}

const activeFeatureHighlightIdAtom = atom<FeatureHighlightIds>()
const activeFeatureHighlightsAtom = atom<FeatureHighlightIds[]>([])

export const useFeatureHighlights = () => {
  const [dismissedFeatureHighlights, setDismissedFeatureHighlights] = useGlobalStore<FeatureHighlightIds[]>(
    'general',
    'featureHighlights.dismissed',
    [],
  )
  const [activeFeatureHighlightId, setActiveFeatureHighlightId] = useAtom(activeFeatureHighlightIdAtom)
  const [activeFeatureHighlights, setActiveFeatureHighlights] = useAtom(activeFeatureHighlightsAtom)

  const setActiveId = useCallback(
    (id: FeatureHighlightIds) => {
      if (dismissedFeatureHighlights.includes(id)) {
        setActiveFeatureHighlights((prev) => prev.filter((id) => id !== activeFeatureHighlightId))
        setActiveFeatureHighlightId(activeFeatureHighlights.filter((id) => id !== activeFeatureHighlightId)[0])
        return
      }
      setActiveFeatureHighlightId(id)
    },
    [
      dismissedFeatureHighlights,
      activeFeatureHighlightId,
      activeFeatureHighlights,
      setActiveFeatureHighlights,
      setActiveFeatureHighlightId,
    ],
  )

  const queueFeatureHighlight = useCallback(
    (id: FeatureHighlightIds, props?: Partial<FeatureHighlightProps>) => {
      if (dismissedFeatureHighlights.includes(id)) {
        return
      }

      setActiveFeatureHighlights((prev) => {
        if (prev.includes(id)) {
          return prev
        }
        const ids = insertIdInOrder(prev, id, props)
        setActiveId(ids[0])
        return ids
      })
    },
    [setActiveId, setActiveFeatureHighlights, dismissedFeatureHighlights, activeFeatureHighlights],
  )

  const dismissFeatureHighlight = useCallback(
    (id: FeatureHighlightIds, props?: Partial<FeatureHighlightProps>) => {
      if (!dismissedFeatureHighlights.includes(id)) {
        setDismissedFeatureHighlights([...dismissedFeatureHighlights, id])

        setActiveFeatureHighlights((prev) => prev.filter((id) => id !== activeFeatureHighlightId))
        if (activeFeatureHighlightId === id) {
          // If nextId is provided and not dismissed, activate it
          if (props?.nextId && !dismissedFeatureHighlights.includes(props.nextId)) {
            setActiveFeatureHighlightId(props.nextId)
          } else {
            // Otherwise, activate the next available highlight
            setActiveFeatureHighlightId(activeFeatureHighlights.filter((id) => id !== activeFeatureHighlightId)[0])
          }
        }
      }
    },
    [
      dismissedFeatureHighlights,
      activeFeatureHighlightId,
      activeFeatureHighlights,
      setActiveFeatureHighlightId,
      setActiveFeatureHighlights,
    ],
  )

  return useMemo(() => {
    return {
      activeFeatureHighlightId,
      queueFeatureHighlight,
      dismissFeatureHighlight,
    }
  }, [activeFeatureHighlights, queueFeatureHighlight, dismissFeatureHighlight])
}

function insertIdInOrder(
  existingIds: FeatureHighlightIds[],
  newId: FeatureHighlightIds,
  props?: Partial<FeatureHighlightProps>,
) {
  const indexNext = props?.nextId !== undefined ? existingIds.indexOf(props.nextId) : -1
  const indexPrev = props?.previousId !== undefined ? existingIds.indexOf(props.previousId) : -1

  // Clone to avoid mutating original
  const newList = [...existingIds]

  if (indexPrev !== -1 && indexNext !== -1 && indexNext === indexPrev + 1) {
    // Ideal slot: previous is directly before next
    newList.splice(indexNext, 0, newId)
  } else if (indexPrev !== -1) {
    // Insert after previousId
    newList.splice(indexPrev + 1, 0, newId)
  } else if (indexNext !== -1) {
    // Insert before nextId
    newList.splice(indexNext, 0, newId)
  } else {
    // No reference, push to end
    newList.push(newId)
  }

  return newList
}
